/**
 * waitForDesktopReady
 *
 * Both desktop-switch reveal paths — the View Transition wrapper
 * (desktopSlideTransition.ts's `waitForNextPaint`) and the swipe-drag
 * overlay's post-commit hold (DesktopSwipeDragLayer.tsx's `commitDrag`) — used
 * to guess how long a remount takes with a fixed timer instead of checking
 * whether it's actually done. That means an already-warm Desktop (still in
 * desktopWarmCache's LRU) sits behind the same fixed wait as a genuinely cold
 * one, and a slow cold one can still get revealed before its content has
 * landed.
 *
 * This resolves as soon as spaceStore's projection for `spaceId` has actually
 * landed — `activeSpace.id === spaceId`, which spaceCRDTService's warm-
 * reactivation path (`_projectDocToStore`) and its cold-load path both set
 * atomically together with `windows` (see spaceStore.ts's `initialize`) — plus
 * one extra animation frame to let React commit and the browser paint that
 * state. It never waits past `timeoutMs`, so a slow/cold switch is bounded
 * exactly like the fixed timer it replaces; a warm one typically resolves
 * well before that ceiling.
 */

import useSpaceStore from '../stores/spaceStore';

function resolveAfterNextPaint(resolve: () => void): void {
  // Two rAFs: the first fires before the browser paints the frame React just
  // committed in reaction to the store update; the second is guaranteed to
  // run after that paint has happened.
  requestAnimationFrame(() => {
    requestAnimationFrame(resolve);
  });
}

export function waitForDesktopReady(spaceId: string, timeoutMs: number): Promise<void> {
  return new Promise<void>((resolve) => {
    let settled = false;
    let unsubscribe: (() => void) | null = null;

    const finish = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      unsubscribe?.();
      resolve();
    };

    const timeoutId = setTimeout(finish, timeoutMs);

    if (useSpaceStore.getState().activeSpace?.id === spaceId) {
      resolveAfterNextPaint(finish);
      return;
    }

    unsubscribe = useSpaceStore.subscribe((state) => {
      if (state.activeSpace?.id === spaceId) {
        resolveAfterNextPaint(finish);
      }
    });
  });
}
